import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useBuilder } from '../lib/builderStore';
import { Badge, Button, Field, Input, Select, Spinner } from '../components/ui';
import { useConfirm } from '../components/Dialog';
import { Icon } from '../components/icons';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';
import { LANGUAGES, isRtlLanguage, languageName } from '../lib/languages';
import type { TranslatableKeyDto } from '../lib/types';

/**
 * The translation editor.
 *
 * The list of translatable strings comes from the server, not from walking the definition
 * here. A second implementation of "what can be translated" would drift from the one the
 * publish check uses, and the drift would show up as a translator carefully filling in a
 * key that then fails to publish — or never being shown a string that needed doing.
 *
 * Edits are written into the definition through the builder store, so they get the same
 * autosave, undo and conflict handling as every other change to a form.
 */
export function Translations() {
  const { formId = '' } = useParams();
  const confirm = useConfirm();

  const definition = useBuilder((s) => s.definition);
  const loadedFormId = useBuilder((s) => s.formId);
  const load = useBuilder((s) => s.load);
  const apply = useBuilder((s) => s.apply);
  const dirty = useBuilder((s) => s.dirty);
  const markSaved = useBuilder((s) => s.markSaved);

  const [locale, setLocale] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(true);
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);

  const draft = useQuery({
    queryKey: ['definition', formId],
    queryFn: () => api.forms.getDefinition(formId),
    enabled: Boolean(formId),
  });

  const meta = useQuery({
    queryKey: ['translations', formId],
    queryFn: () => api.translations.get(formId),
    enabled: Boolean(formId),
  });

  useEffect(() => {
    if (draft.data && loadedFormId !== formId) load(formId, draft.data);
  }, [draft.data, formId, loadedFormId, load]);

  useEffect(() => {
    if (!dirty || !definition) return;
    const timer = setTimeout(async () => {
      await api.forms.saveDefinition(formId, definition);
      markSaved();
      void meta.refetch();
    }, 1200);
    return () => clearTimeout(timer);
  }, [dirty, definition, formId, markSaved, meta]);

  const others = useMemo(
    () => (definition?.locales ?? []).filter((l) => l !== definition?.defaultLocale),
    [definition],
  );

  useEffect(() => {
    if (!locale && others.length > 0) setLocale(others[0]!);
    if (locale && !others.includes(locale)) setLocale(others[0] ?? null);
  }, [others, locale]);

  if (!definition || meta.isLoading) return <div className="p-8"><Spinner /></div>;

  const keys = meta.data?.keys ?? [];
  const strings = (locale && definition.translations?.[locale]) || {};

  const setString = (key: string, value: string) =>
    apply('Edit translation', (d) => {
      if (!locale) return;
      d.translations ??= {};
      d.translations[locale] ??= {};

      // An empty box means "not translated", not "translated to nothing". Leaving the key
      // present with an empty string would count as done and show a blank to a respondent.
      if (value.trim() === '') delete d.translations[locale][key];
      else d.translations[locale][key] = value;

      // Touching a string is what marks it reviewed. No separate "approve" button to
      // remember, and no way to review something without the flag clearing.
      const flagged = d.machineTranslated?.[locale];
      if (flagged) {
        d.machineTranslated![locale] = flagged.filter((k) => k !== key);
      }
    });

  // Everything not already on the form. Offering a language twice would produce a form
  // with a duplicate locale, which the server accepts and nothing renders twice.
  const available = LANGUAGES.filter((l) => !(definition.locales ?? []).includes(l.code));

  const addLocale = (code: string) => {
    const trimmed = code.trim();
    if (!trimmed || definition.locales?.includes(trimmed)) return;

    apply('Add language', (d) => {
      d.locales = [...(d.locales ?? [d.defaultLocale]), trimmed];
    });

    setPicked('');
    setCustomCode('');
    setLocale(trimmed);
  };

  const removeLocale = async (code: string) => {
    // Removing a language throws away every string translated into it, so it says so.
    const count = Object.keys(definition.translations?.[code] ?? {}).length;
    const name = languageName(code);

    if (!await confirm({
      title: `Remove ${name}?`,
      tone: 'danger',
      confirmLabel: 'Remove language',
      body: count > 0
        ? `The ${count} string${count === 1 ? '' : 's'} translated into it will be deleted.`
        : 'Nothing has been translated into it yet.',
    })) return;

    apply('Remove language', (d) => {
      d.locales = (d.locales ?? []).filter((l) => l !== code);
      if (d.translations) delete d.translations[code];
    });
  };

  const unreviewed = new Set(
    (locale && definition.machineTranslated?.[locale]) || []);

  /**
   * Fills in what is missing from the machine translator.
   *
   * Only the blanks: overwriting a human's work with machine output is the worst thing this
   * button could do, because the work is gone, the result looks finished, and nobody who
   * does not read the language can tell. "Redo everything" is a separate, explicit choice.
   */
  const autoTranslate = async (retranslate: boolean) => {
    if (!locale) return;

    if (retranslate && !await confirm({
      title: `Retranslate every string in ${languageName(locale)}?`,
      tone: 'danger',
      confirmLabel: 'Retranslate everything',
      body: 'Anything written by hand will be replaced by machine output.',
    })) {
      return;
    }

    setBusy(true);
    setNotice(null);

    try {
      const result = await api.translations.auto(formId, locale, retranslate);
      const filled = Object.entries(result.translated);

      if (filled.length === 0) {
        setNotice({ tone: 'info', text: result.message ?? 'Nothing was left to translate.' });
        return;
      }

      // One undoable step, not one per string: an author who does not like the result
      // presses undo once.
      apply(`Translate into ${languageName(locale)}`, (d) => {
        d.translations ??= {};
        d.translations[locale] ??= {};
        d.machineTranslated ??= {};

        const flagged = new Set(d.machineTranslated[locale] ?? []);

        for (const [key, value] of filled) {
          d.translations[locale]![key] = value;
          flagged.add(key);
        }

        d.machineTranslated[locale] = [...flagged];
      });

      // Split, because the progress counter above counts only form content and a bare
      // "translated 36" next to "14 of 14 done" reads as a contradiction.
      const interfaceCount = filled.filter(([key]) => key.startsWith('ui.')).length;
      const contentCount = filled.length - interfaceCount;

      const summary = `Translated ${contentCount} form string${contentCount === 1 ? '' : 's'}`
        + (interfaceCount > 0 ? ` and ${interfaceCount} button and message strings` : '')
        + ` with ${result.provider}.`;

      setNotice({
        tone: 'info',
        text: result.skipped.length === 0
          ? `${summary} Check them before publishing.`
          : `${summary} ${result.skipped.length} were left alone because their placeholders `
            + `would not have survived — write those by hand.`,
      });
    } catch (error) {
      setNotice({
        tone: 'danger',
        text: error instanceof Error ? error.message : 'The translation service failed.',
      });
    } finally {
      setBusy(false);
    }
  };

  const content = keys.filter((k) => !k.isInterface);
  const done = content.filter((k) => (strings[k.key] ?? '').trim() !== '').length;

  const needle = filter.trim().toLowerCase();
  const visible = keys.filter((k) => {
    if (!showDone && (strings[k.key] ?? '').trim() !== '') return false;
    if (!needle) return true;
    return k.source.toLowerCase().includes(needle)
      || k.description.toLowerCase().includes(needle)
      || k.key.toLowerCase().includes(needle);
  });

  const grouped = [
    { title: 'Form content', items: visible.filter((k) => !k.isInterface) },
    { title: 'Buttons and messages', items: visible.filter((k) => k.isInterface) },
  ].filter((g) => g.items.length > 0);

  return (
    <Page width="wide">
      <PageHeader
        icon="globe"
        title="Translations"
        description={`Respondents see ${definition.defaultLocale} unless they ask for another
                      language, or their browser prefers one this form offers.`}
      />

      <Stack>
        <Section title="Languages" hint="The default is what anything untranslated falls back to.">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="primary">
              {languageName(definition.defaultLocale)} · default
            </Badge>

            {others.map((code) => {
              const progress = meta.data?.progress?.[code];
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1.5 rounded-full border
                             border-[var(--ff-border)] py-1 pl-3 pr-1.5 text-xs"
                >
                  {languageName(code)}
                  <span className="font-mono text-[10px] text-[var(--ff-text-muted)]">{code}</span>
                  {isRtlLanguage(code) && (
                    <span className="text-[var(--ff-text-muted)]">rtl</span>
                  )}
                  {progress && (
                    <span className="text-[var(--ff-text-muted)]">
                      {progress.done}/{progress.total}
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${languageName(code)}`}
                    onClick={() => removeLocale(code)}
                    className="rounded-full px-1 text-[var(--ff-text-muted)]
                               hover:text-[var(--ff-danger)]"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <Field label="Add a language">
              <Select
                value={picked}
                onChange={(e) => {
                  setPicked(e.target.value);
                  // Chosen from the list: nothing more to type, so add it straight away.
                  if (e.target.value && e.target.value !== 'other') addLocale(e.target.value);
                }}
                className="w-72"
              >
                <option value="">Choose a language…</option>
                {available.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.native}
                    {language.native === language.english ? '' : ` — ${language.english}`}
                    {` (${language.code})`}
                  </option>
                ))}
                {/* The list is a convenience, not a limit: the server accepts any
                    well-formed code, and somebody translating into a language nobody
                    anticipated should not have to wait for a release. */}
                <option value="other">Other…</option>
              </Select>
            </Field>

            {picked === 'other' && (
              <>
                <Field label="Language code" hint="Such as cy, eu or fr-BE.">
                  <Input
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addLocale(customCode); }}
                    placeholder="cy"
                    className="w-32"
                  />
                </Field>
                <Button onClick={() => addLocale(customCode)} disabled={!customCode.trim()}>
                  <Icon name="plus" size={14} /> Add
                </Button>
              </>
            )}
          </div>
        </Section>

        {others.length === 0 ? (
          <Notice tone="info">
            This form is in {definition.defaultLocale} only. Add a language above to start
            translating it.
          </Notice>
        ) : (
          <Section
            title={`Translating into ${languageName(locale ?? '')}`}
            hint={`${done} of ${content.length} form strings done. Anything left blank is
                   shown in ${definition.defaultLocale}, so a part-finished translation is
                   safe to publish.`}
          >
            {notice && (
              <div className="mb-3">
                <Notice tone={notice.tone}>{notice.text}</Notice>
              </div>
            )}

            {meta.data?.provider === 'pseudo' && (
              <div className="mb-3">
                <Notice tone="warning">
                  This server is set up with <strong>pseudo-translation</strong>, which
                  brackets each string rather than translating it. It is for finding
                  untranslated text and layouts that break, not for publishing.
                </Notice>
              </div>
            )}

            <div className="mb-3 flex flex-wrap items-end gap-3">
              <Field label="Language">
                <Select value={locale ?? ''} onChange={(e) => setLocale(e.target.value)}>
                  {others.map((code) => (
                    <option key={code} value={code}>{languageName(code)} ({code})</option>
                  ))}
                </Select>
              </Field>

              <Field label="Search">
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter by text or key"
                />
              </Field>

              <label className="flex items-center gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--ff-primary)]"
                  checked={!showDone}
                  onChange={(e) => setShowDone(!e.target.checked)}
                />
                Only what is left
              </label>

              {meta.data?.canAutoTranslate && (
                <div className="flex gap-2 pb-1">
                  <Button
                    variant="primary"
                    onClick={() => autoTranslate(false)}
                    disabled={busy || done === content.length}
                  >
                    <Icon name="sparkle" size={14} />
                    {busy ? 'Translating…' : 'Fill in the rest'}
                  </Button>

                  {done > 0 && (
                    <Button onClick={() => autoTranslate(true)} disabled={busy}>
                      Redo everything
                    </Button>
                  )}
                </div>
              )}
            </div>

            {grouped.length === 0 && (
              <p className="py-6 text-center text-sm text-[var(--ff-text-muted)]">
                {showDone ? 'Nothing matches that.' : 'Everything is translated.'}
              </p>
            )}

            {grouped.map((group) => (
              <div key={group.title} className="mb-5">
                <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest
                               text-[var(--ff-text-muted)]">
                  {group.title}
                </h3>

                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <TranslationRow
                      key={item.key}
                      item={item}
                      value={strings[item.key] ?? ''}
                      unreviewed={unreviewed.has(item.key)}
                      rtl={isRtlLanguage(locale ?? '')}
                      onChange={(value) => setString(item.key, value)}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </Section>
        )}
      </Stack>
    </Page>
  );
}

function TranslationRow({
  item, value, rtl, unreviewed, onChange,
}: {
  item: TranslatableKeyDto;
  value: string;
  rtl: boolean;
  unreviewed: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <li className="grid gap-2 rounded-lg border border-[var(--ff-border)] p-2.5
                   sm:grid-cols-2">
      <div className="min-w-0">
        <p className="text-[11px] text-[var(--ff-text-muted)]">{item.description}</p>
        <p className="break-words text-sm">{item.source}</p>
      </div>

      <div>
        {/* Machine output is good enough to publish and not good enough to trust silently:
            the difference between "Submit" and "Surrender" is invisible to somebody who
            does not read the language. Editing the box clears the flag. */}
        {unreviewed && (
          <span className="mb-1 inline-flex items-center gap-1 text-[11px]
                           text-[var(--ff-warning, var(--ff-text-muted))]">
            <Icon name="sparkle" size={11} /> machine translated — not checked
          </span>
        )}
        <Input
          value={value}
          // The direction of the language being typed, not the studio's. Without this an
          // Arabic translation is entered left to right and reads wrongly to the person
          // writing it, which is when mistakes get made.
          dir={rtl ? 'rtl' : undefined}
          aria-label={`${item.description} — translation`}
          placeholder="Not translated"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </li>
  );
}
